import type { Block } from '../lib/types';
import { Area, Field, IconBtn, Lines, Select, Text } from './ui';

/**
 * Editor for the eight lesson block types.
 *
 * The union is defined once on the server (`api/src/schemas/block.ts`) and mirrored in
 * `lib/types.ts`; this switch is the third place that knows the shape, so a new block
 * type means touching all three. That is deliberate — the alternative is a generic
 * key/value editor, which would let an author save a block the renderer cannot draw.
 *
 * Text is plain everywhere. No block accepts HTML, which is what keeps the CMS free of
 * stored XSS without a sanitiser (DEVELOPER_GUIDE.md §5). Do not add a rich-text field
 * here without solving that first.
 */

const LABELS: Record<Block['t'], string> = {
  p: 'Paragraph',
  h: 'Subheading',
  ul: 'Bulleted list',
  ol: 'Numbered list',
  note: 'Callout',
  do: 'Do / Don’t',
  stat: 'Label / value rows',
  quote: 'Quote',
};

/** A usable empty block per type, so adding one never starts in an invalid state. */
function blank(t: Block['t']): Block {
  switch (t) {
    case 'p':
      return { t: 'p', x: '' };
    case 'h':
      return { t: 'h', x: '' };
    case 'ul':
      return { t: 'ul', items: [''] };
    case 'ol':
      return { t: 'ol', items: [''] };
    case 'note':
      return { t: 'note', tone: 'tip', title: '', x: '' };
    case 'do':
      return { t: 'do', dos: [''], donts: [''] };
    case 'stat':
      return { t: 'stat', items: [{ k: '', v: '' }] };
    case 'quote':
      return { t: 'quote', x: '', who: '' };
  }
}

function BlockBody({ block, onChange }: { block: Block; onChange: (b: Block) => void }) {
  switch (block.t) {
    case 'p':
      return (
        <Area value={block.x} onChange={(x) => onChange({ ...block, x })} rows={4} placeholder="Body text…" />
      );

    case 'h':
      return <Text value={block.x} onChange={(x) => onChange({ ...block, x })} placeholder="Subheading" />;

    case 'ul':
    case 'ol':
      return <Lines items={block.items} onChange={(items) => onChange({ ...block, items })} placeholder="List item" />;

    case 'note':
      return (
        <>
          <Field label="Tone">
            <Select
              value={block.tone}
              onChange={(tone) => onChange({ ...block, tone })}
              options={[
                { value: 'tip', label: 'Tip (green)' },
                { value: 'warn', label: 'Warning (red)' },
                { value: 'info', label: 'Info (blue)' },
              ]}
            />
          </Field>
          <Field label="Title">
            <Text value={block.title} onChange={(title) => onChange({ ...block, title })} />
          </Field>
          <Field label="Body">
            <Area value={block.x} onChange={(x) => onChange({ ...block, x })} rows={3} />
          </Field>
        </>
      );

    case 'do':
      return (
        <div className="vu-grid vu-grid-2" style={{ gap: 'var(--space-4)' }}>
          <Field label="Do">
            <Lines items={block.dos} onChange={(dos) => onChange({ ...block, dos })} placeholder="Do this" />
          </Field>
          <Field label="Don’t">
            <Lines items={block.donts} onChange={(donts) => onChange({ ...block, donts })} placeholder="Avoid this" />
          </Field>
        </div>
      );

    case 'stat': {
      // Key/value pairs, not bare strings — the renderer draws a two-column table.
      const set = (i: number, patch: Partial<{ k: string; v: string }>) =>
        onChange({ ...block, items: block.items.map((row, j) => (j === i ? { ...row, ...patch } : row)) });
      return (
        <div>
          {block.items.map((row, i) => (
            <div key={i} style={{ display: 'flex', gap: 6, marginBottom: 6 }}>
              <input className="vu-input" value={row.k} placeholder="Label" onChange={(e) => set(i, { k: e.target.value })} />
              <input className="vu-input" value={row.v} placeholder="Value" onChange={(e) => set(i, { v: e.target.value })} />
              <IconBtn
                label="Remove row"
                danger
                onClick={() => onChange({ ...block, items: block.items.filter((_, j) => j !== i) })}
              >
                ×
              </IconBtn>
            </div>
          ))}
          <button
            type="button"
            className="vu-btn vu-btn-ghost"
            onClick={() => onChange({ ...block, items: [...block.items, { k: '', v: '' }] })}
          >
            + Add row
          </button>
        </div>
      );
    }

    case 'quote':
      return (
        <>
          <Field label="Quote">
            <Area value={block.x} onChange={(x) => onChange({ ...block, x })} rows={3} />
          </Field>
          <Field label="Attributed to">
            <Text value={block.who} onChange={(who) => onChange({ ...block, who })} placeholder="Name, role" />
          </Field>
        </>
      );
  }
}

export function BlockEditor({
  blocks,
  onChange,
}: {
  blocks: Block[];
  onChange: (next: Block[]) => void;
}) {
  const set = (i: number, b: Block) => onChange(blocks.map((x, j) => (j === i ? b : x)));
  const remove = (i: number) => onChange(blocks.filter((_, j) => j !== i));
  const move = (i: number, d: -1 | 1) => {
    const j = i + d;
    if (j < 0 || j >= blocks.length) return;
    const next = [...blocks];
    [next[i], next[j]] = [next[j], next[i]];
    onChange(next);
  };

  return (
    <div>
      {blocks.map((block, i) => (
        <div
          key={i}
          className="vu-card"
          style={{ padding: 'var(--space-4)', marginBottom: 'var(--space-3)', background: 'var(--surface-sunken)' }}
        >
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 'var(--space-3)' }}>
            <span className="vu-kicker" style={{ margin: 0 }}>
              {i + 1}. {LABELS[block.t]}
            </span>
            <span style={{ flex: 1 }} />
            <IconBtn label="Move block up" onClick={() => move(i, -1)} disabled={i === 0}>↑</IconBtn>
            <IconBtn label="Move block down" onClick={() => move(i, 1)} disabled={i === blocks.length - 1}>↓</IconBtn>
            <IconBtn label="Delete block" onClick={() => remove(i)} danger>×</IconBtn>
          </div>
          <BlockBody block={block} onChange={(b) => set(i, b)} />
        </div>
      ))}

      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginTop: 'var(--space-3)' }}>
        {(Object.keys(LABELS) as Block['t'][]).map((t) => (
          <button
            key={t}
            type="button"
            className="vu-btn vu-btn-ghost"
            onClick={() => onChange([...blocks, blank(t)])}
          >
            + {LABELS[t]}
          </button>
        ))}
      </div>
    </div>
  );
}
